import React from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import AgentToolLoadout from "../../AgentToolLoadout";
import AgentConfigSection from "../AgentConfigSection";
import McpToolsSection from "./McpToolsSection";
import PeoplePicker from "../../../common/PeoplePicker";
import {
  SEND_SLACK_COMPONENT_TOOL_NAME,
  filterToolCategories,
} from "../toolCatalog";
import {
  buildSlackWebhookValidationError,
  parseSlackWebhookUrls,
} from "../toolConfigValidation";
import {
  normalizeConfiguredAgents,
  normalizeConfiguredTables,
} from "../agentConfigDraftModel";

export default function ToolsTab(props) {
  const ResolvedPeoplePicker =
    typeof PeoplePicker === "function" ? PeoplePicker : null;
  const {
    name,
    enabledTools,
    setEnabledTools,
    toggleTool,
    setCategoryToolsEnabled,
    toolInstructions,
    setToolInstructions,
    sendSlackWebhookUrlsInput,
    setSendSlackWebhookUrlsInput,
    sendSlackWebhookValidationError,
    setSendSlackWebhookValidationError,
    toolCategories,
    tableScopedToolNames,
    toolSearchQuery,
    setToolSearchQuery,
    loadingToolCatalog,
    runWorkflowConfigs,
    setRunWorkflowConfigs,
    workflowScopeMode,
    setWorkflowScopeMode,
    runWorkflowValidationError,
    setRunWorkflowValidationError,
    configuredTables,
    setConfiguredTables,
    configuredAgents,
    setConfiguredAgents,
    tableScopeMode,
    setTableScopeMode,
    agentScopeMode,
    setAgentScopeMode,
    configuredTablesValidationError,
    setConfiguredTablesValidationError,
    workflowOptions,
    loadingWorkflowOptions,
    agentOptions,
    loadingAgentOptions,
    reviewEscalationPolicy,
    setReviewEscalationPolicy,
    blockedWhileReviewMessage,
    setBlockedWhileReviewMessage,
    customBlockedWhileReviewMessageEnabled,
    setCustomBlockedWhileReviewMessageEnabled,
    autoReplyOnBlockedIngress,
    setAutoReplyOnBlockedIngress,
    reviewTaskAssigneeUserId,
    setReviewTaskAssigneeUserId,
    reviewTaskEmailNotificationEnabled,
    setReviewTaskEmailNotificationEnabled,
    reviewTaskEligibleUsers,
    loadingReviewTaskEligibleUsers,
    databaseList,
    loadingDatabases,
    agentId,
    saving,
    mcpServerRefs,
    setMcpServerRefs,
    approvalRequiredTools,
    onToggleToolApproval,
  } = props;

  const tableScopeEnabled = enabledTools.some((toolName) =>
    tableScopedToolNames.has(toolName)
  );
  const sendSlackComponentEnabled = enabledTools.includes(
    SEND_SLACK_COMPONENT_TOOL_NAME
  );
  const normalizedSendSlackWebhookUrls = parseSlackWebhookUrls(
    sendSlackWebhookUrlsInput
  );
  const liveSlackWebhookValidationError = sendSlackComponentEnabled
    ? buildSlackWebhookValidationError(normalizedSendSlackWebhookUrls)
    : "";
  const slackWebhookValidationMessage =
    sendSlackWebhookValidationError || liveSlackWebhookValidationError;
  const workflowById = new Map();
  workflowOptions.forEach((option) => {
    if (option?.workflowId) workflowById.set(option.workflowId, option);
  });
  runWorkflowConfigs.forEach((option) => {
    if (option?.workflowId && !workflowById.has(option.workflowId)) {
      workflowById.set(option.workflowId, option);
    }
  });
  const selectableWorkflowOptions = Array.from(workflowById.values());
  const selectedWorkflowValues = runWorkflowConfigs
    .map((cfg) => workflowById.get(cfg.workflowId) || cfg)
    .filter((cfg) => !!cfg?.workflowId);
  const tableById = new Map();
  const normalizedConfiguredTableScope =
    normalizeConfiguredTables(configuredTables);
  databaseList.forEach((option) => {
    const tableId = String(option?.databaseId || option?.tableId || "").trim();
    if (!tableId) return;
    tableById.set(tableId, {
      tableId,
      tableName: option?.name || option?.tableName || tableId,
    });
  });
  normalizedConfiguredTableScope.forEach((option) => {
    if (option?.tableId && !tableById.has(option.tableId)) {
      tableById.set(option.tableId, option);
    }
  });
  const selectableTableOptions = Array.from(tableById.values());
  const selectedTableValues = normalizedConfiguredTableScope
    .map((cfg) => tableById.get(cfg.tableId) || cfg)
    .filter((cfg) => !!cfg?.tableId);
  const agentById = new Map();
  agentOptions.forEach((option) => {
    if (option?.agentId) agentById.set(option.agentId, option);
  });
  configuredAgents.forEach((option) => {
    if (option?.agentId && !agentById.has(option.agentId)) {
      agentById.set(option.agentId, option);
    }
  });
  const selectableAgentOptions = Array.from(agentById.values()).filter(
    (agent) => agent?.agentId !== agentId
  );
  const selectedAgentValues = normalizeConfiguredAgents(configuredAgents)
    .map((cfg) => agentById.get(cfg.agentId) || cfg)
    .filter((cfg) => !!cfg?.agentId);
  const filteredToolCategories = filterToolCategories(
    toolCategories,
    toolSearchQuery
  );
  const hasToolSearchQuery = toolSearchQuery.trim().length > 0;
  const matchingToolCount = filteredToolCategories.reduce(
    (total, category) => total + category.tools.length,
    0
  );
  // Any tool the agent has previously enabled that no longer shows up in
  // the tenant's current catalog — typically a Custom Function that was
  // soft-deleted or had its agent-tool exposure flipped off. The registered
  // stub adapter still responds with "disabled" at invocation time, but
  // surfacing it here lets the user proactively drop it from enabledTools.
  const catalogToolNames = new Set();
  toolCategories.forEach((cat) => {
    (cat?.tools || []).forEach((tool) => {
      if (tool?.name) catalogToolNames.add(tool.name);
    });
  });
  const staleEnabledTools =
    !loadingToolCatalog && toolCategories.length > 0
      ? enabledTools.filter(
          (toolName) => toolName && !catalogToolNames.has(toolName)
        )
      : [];
  const reviewTaskEnabled = enabledTools.includes("review_task");
  const renderReviewTaskControls = () => (
    <Box
      sx={{
        mt: 1.25,
        ml: 1.5,
        pl: 1.5,
        pr: 0.5,
        borderLeft: 2,
        borderColor: "divider",
      }}
    >
      <Typography className="org-field-label" sx={{ mb: 0.5 }}>
        Create Task Controls
      </Typography>
      <Typography sx={{ fontSize: "11px", color: "text.secondary", mb: 1 }}>
        Create Tasks always go to Task Inbox. Email is optional additional
        notification and can only target active users in this account.
      </Typography>
      {ResolvedPeoplePicker ? (
        <ResolvedPeoplePicker
          label="Assigned user"
          placeholder="Search people..."
          items={reviewTaskEligibleUsers}
          selectedIds={
            reviewTaskAssigneeUserId.trim() ? [reviewTaskAssigneeUserId] : []
          }
          loading={loadingReviewTaskEligibleUsers}
          onToggle={(userName) =>
            setReviewTaskAssigneeUserId(
              reviewTaskAssigneeUserId === userName ? "" : userName
            )
          }
          getId={(user) => user.userName}
          getName={(user) => user.displayName || user.userName}
          getDetail={(user) => user.email || ""}
          getAvatarLabel={(user) => user.displayName || user.userName || "User"}
          emptyStateLabel="No active users found in this account."
        />
      ) : (
        <TextField
          className="org-styled-textfield"
          select
          fullWidth
          size="small"
          value={reviewTaskAssigneeUserId}
          onChange={(e) => setReviewTaskAssigneeUserId(e.target.value)}
          disabled={loadingReviewTaskEligibleUsers}
          error={!reviewTaskAssigneeUserId.trim() && saving}
          helperText={
            loadingReviewTaskEligibleUsers
              ? "Loading active users..."
              : "Select one active user as the default assignee."
          }
        >
          <MenuItem value="">Select assignee...</MenuItem>
          {(Array.isArray(reviewTaskEligibleUsers)
            ? reviewTaskEligibleUsers
            : []
          ).map((user) => (
            <MenuItem key={user.userName} value={user.userName}>
              {user.displayName || user.userName}
              {user.email ? ` (${user.email})` : ""}
            </MenuItem>
          ))}
        </TextField>
      )}
      <Typography
        sx={{
          fontSize: "11px",
          color:
            !reviewTaskAssigneeUserId.trim() && saving
              ? "error.main"
              : "text.disabled",
          mt: 0.5,
          mb: 1,
        }}
      >
        {!reviewTaskAssigneeUserId.trim() && saving
          ? "Required. Select an internal account user."
          : "Select exactly one internal assignee."}
      </Typography>
      <Divider sx={{ borderColor: "divider", my: 1.25 }} />
      <Typography className="org-field-label" sx={{ mb: 0.5 }}>
        Escalation rules
      </Typography>
      <Typography sx={{ fontSize: "11px", color: "text.secondary", mb: 1 }}>
        Define when the agent should escalate to human review and what details
        must be included for the assignee.
      </Typography>
      <TextField
        className="org-styled-textfield"
        fullWidth
        multiline
        minRows={4}
        size="small"
        value={reviewEscalationPolicy}
        onChange={(e) => setReviewEscalationPolicy(e.target.value)}
        placeholder="Describe when the AI should create a Create Task and what context should be included."
        helperText="Optional. Guides the agent on when to escalate to human review."
        sx={{ mb: 1 }}
      />
      <Divider sx={{ borderColor: "divider", my: 1.25 }} />
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={reviewTaskEmailNotificationEnabled}
            onChange={(e) =>
              setReviewTaskEmailNotificationEnabled(e.target.checked)
            }
          />
        }
        label="Also send task link by email"
        sx={{ color: "text.secondary", mb: 0.5 }}
      />
      <Typography
        sx={{
          fontSize: "11px",
          color:
            reviewTaskEmailNotificationEnabled &&
            !reviewTaskAssigneeUserId.trim() &&
            saving
              ? "error.main"
              : "text.disabled",
          mt: 0.5,
          mb: 1,
        }}
      >
        {reviewTaskEmailNotificationEnabled &&
        !reviewTaskAssigneeUserId.trim() &&
        saving
          ? "Select an assigned user to enable email notifications."
          : "Optional extra channel. Emails go to the assigned user above."}
      </Typography>
      <Divider sx={{ borderColor: "divider", my: 1.25 }} />
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={customBlockedWhileReviewMessageEnabled}
            onChange={(e) =>
              setCustomBlockedWhileReviewMessageEnabled(e.target.checked)
            }
          />
        }
        label="Customize blocked message (chat + blocked email auto-replies)"
        sx={{
          color: "text.secondary",
          mb: customBlockedWhileReviewMessageEnabled ? 0.25 : 0,
        }}
      />
      <Typography
        sx={{
          fontSize: "11px",
          color: "text.secondary",
          mb: customBlockedWhileReviewMessageEnabled ? 0.75 : 0,
        }}
      >
        This message is shown in blocked chat sessions and is also used for
        blocked email auto-replies when enabled below.
      </Typography>
      {customBlockedWhileReviewMessageEnabled && (
        <TextField
          className="org-styled-textfield"
          fullWidth
          multiline
          minRows={2}
          size="small"
          value={blockedWhileReviewMessage}
          onChange={(e) => setBlockedWhileReviewMessage(e.target.value)}
          placeholder="Custom blocked message shown while the session is under review."
          helperText={
            autoReplyOnBlockedIngress
              ? "Used in blocked chat sessions and blocked email auto-replies."
              : "Used in blocked chat sessions. Email auto-replies are disabled below."
          }
          sx={{ mb: 1 }}
        />
      )}
      <Divider sx={{ borderColor: "divider", my: 1.25 }} />
      <FormControlLabel
        control={
          <Checkbox
            size="small"
            checked={autoReplyOnBlockedIngress}
            onChange={(e) => setAutoReplyOnBlockedIngress(e.target.checked)}
          />
        }
        label="Auto-reply on blocked non-UI ingress (email/inter-agent/scheduled)"
        sx={{ color: "text.secondary" }}
      />
      <Typography sx={{ fontSize: "11px", color: "text.disabled", mt: 0.5 }}>
        Controls blocked email auto-replies and uses the blocked message above.
      </Typography>
    </Box>
  );

  const renderTableScopeConfig = () => (
    <Box>
      <Typography className="org-field-label" sx={{ mb: 0.5 }}>
        Table Tool Scope
      </Typography>
      <TextField
        className="org-styled-textfield"
        select
        fullWidth
        size="small"
        value={tableScopeMode}
        onChange={(e) => setTableScopeMode(e.target.value)}
        helperText="Choose whether table tools can use all current and future tables, or only selected tables."
        sx={{ mb: 1 }}
      >
        <MenuItem value="ALL">All current and future tables</MenuItem>
        <MenuItem value="SELECTED">Selected tables only</MenuItem>
      </TextField>
      {tableScopeMode === "ALL" ? (
        <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>
          All current and future tables are included. The agent should use
          list_tables and get_table to discover exact table IDs.
        </Typography>
      ) : (
        <Autocomplete
          multiple
          options={selectableTableOptions}
          value={selectedTableValues}
          loading={loadingDatabases}
          getOptionLabel={(option) => option.tableName || option.tableId || ""}
          isOptionEqualToValue={(option, value) =>
            option.tableId === value.tableId
          }
          onChange={(_, newValue) => {
            setConfiguredTables(normalizeConfiguredTables(newValue));
            setConfiguredTablesValidationError("");
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              className="org-styled-textfield"
              variant="outlined"
              size="small"
              placeholder="Select tables for table tools..."
              error={Boolean(configuredTablesValidationError)}
              helperText={
                configuredTablesValidationError ||
                "Shared across all table tools. Scopes what tables this agent can read/write."
              }
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.tableId}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography sx={{ fontSize: "13px", color: "text.secondary" }}>
                  {option.tableName || option.tableId}
                </Typography>
                <Typography sx={{ fontSize: "11px", color: "text.disabled" }}>
                  {option.tableId}
                </Typography>
              </Box>
            </li>
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.tableId}
                label={option.tableName || option.tableId}
                size="small"
                sx={{ fontSize: "12px" }}
              />
            ))
          }
          size="small"
        />
      )}
    </Box>
  );

  const renderRunWorkflowConfig = () => (
    <Box>
      <Typography className="org-field-label" sx={{ mb: 0.5 }}>
        Run Workflow Tool Scope
      </Typography>
      <TextField
        className="org-styled-textfield"
        select
        fullWidth
        size="small"
        value={workflowScopeMode}
        onChange={(e) => setWorkflowScopeMode(e.target.value)}
        helperText="Choose whether run_workflow can use all current and future workflows, or only selected workflows."
        sx={{ mb: 1 }}
      >
        <MenuItem value="ALL">All current and future workflows</MenuItem>
        <MenuItem value="SELECTED">Selected workflows only</MenuItem>
      </TextField>
      {workflowScopeMode === "ALL" ? (
        <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>
          All current and future workflows are included. The agent should use
          list_workflows and read_workflow to discover exact workflow IDs.
        </Typography>
      ) : (
        <Autocomplete
          multiple
          options={selectableWorkflowOptions}
          value={selectedWorkflowValues}
          loading={loadingWorkflowOptions}
          getOptionLabel={(option) =>
            option.workflowName || option.workflowId || ""
          }
          isOptionEqualToValue={(option, value) =>
            option.workflowId === value.workflowId
          }
          onChange={(_, newValue) => {
            setRunWorkflowConfigs(
              newValue
                .filter((wf) => !!wf?.workflowId)
                .map((wf) => ({
                  workflowId: wf.workflowId,
                  workflowName: wf.workflowName || wf.workflowId,
                  workflowDescription: wf.workflowDescription || "",
                  published: wf.published === true,
                }))
            );
            setRunWorkflowValidationError("");
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              className="org-styled-textfield"
              variant="outlined"
              size="small"
              placeholder="Select workflows for run_workflow..."
              error={Boolean(runWorkflowValidationError)}
              helperText={
                runWorkflowValidationError ||
                "Choose one or more workflows this agent is allowed to run."
              }
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.workflowId}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography sx={{ fontSize: "13px", color: "text.secondary" }}>
                  {option.workflowName || option.workflowId}
                </Typography>
                <Typography sx={{ fontSize: "11px", color: "text.disabled" }}>
                  {option.workflowId}
                </Typography>
              </Box>
            </li>
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.workflowId}
                label={option.workflowName || option.workflowId}
                size="small"
                sx={{ fontSize: "12px" }}
              />
            ))
          }
          size="small"
        />
      )}
    </Box>
  );

  const renderAgentScopeConfig = () => (
    <Box sx={{ mb: 2 }}>
      <Typography className="org-field-label" sx={{ mb: 0.5 }}>
        Agent Interaction Scope
      </Typography>
      <TextField
        className="org-styled-textfield"
        select
        fullWidth
        size="small"
        value={agentScopeMode}
        onChange={(e) => setAgentScopeMode(e.target.value)}
        helperText="Choose which agents can be discovered or messaged by agent tools."
        sx={{ mb: 1 }}
      >
        <MenuItem value="ALL">All current and future agents</MenuItem>
        <MenuItem value="SELECTED">Selected agents only</MenuItem>
      </TextField>
      {agentScopeMode === "ALL" ? (
        <Typography sx={{ fontSize: "12px", color: "text.secondary" }}>
          All current and future customer agents are included.
        </Typography>
      ) : (
        <Autocomplete
          multiple
          options={selectableAgentOptions}
          value={selectedAgentValues}
          loading={loadingAgentOptions}
          getOptionLabel={(option) => option.name || option.agentId || ""}
          isOptionEqualToValue={(option, value) =>
            option.agentId === value.agentId
          }
          onChange={(_, newValue) => {
            setConfiguredAgents(normalizeConfiguredAgents(newValue));
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              className="org-styled-textfield"
              variant="outlined"
              size="small"
              placeholder="Select agents..."
              helperText="Scopes list_agents, list_agent_sessions, and send_message."
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.agentId}
                label={option.name || option.agentId}
                size="small"
                sx={{ fontSize: "12px" }}
              />
            ))
          }
          size="small"
        />
      )}
    </Box>
  );

  const renderSlackWebhooksConfig = () => (
    <Box>
      <Typography className="org-field-label" sx={{ mb: 0.5 }}>
        Allowed Slack Webhooks
      </Typography>
      <TextField
        className="org-styled-textfield"
        fullWidth
        multiline
        minRows={3}
        size="small"
        value={sendSlackWebhookUrlsInput}
        onChange={(e) => {
          setSendSlackWebhookUrlsInput(e.target.value);
          if (sendSlackWebhookValidationError) {
            setSendSlackWebhookValidationError("");
          }
        }}
        placeholder="https://hooks.slack.com/services/...\nhttps://hooks.slack.com/services/..."
        error={Boolean(slackWebhookValidationMessage)}
        helperText={
          slackWebhookValidationMessage ||
          "Enter one Slack incoming webhook URL per line (or comma-separated)."
        }
      />
    </Box>
  );

  const renderToolConfig = (tool) => {
    if (!tool) return null;
    if (!enabledTools.includes(tool.name)) return null;
    if (tool.name === "run_workflow") {
      return renderRunWorkflowConfig();
    }
    if (tool.name === SEND_SLACK_COMPONENT_TOOL_NAME) {
      return renderSlackWebhooksConfig();
    }
    if (tool.name === "review_task") {
      return (
        <Box>
          <Typography className="org-field-label" sx={{ mb: 0.5 }}>
            Create Task Controls
          </Typography>
          {renderReviewTaskControls()}
        </Box>
      );
    }
    return null;
  };

  const renderCategoryConfig = (category) => {
    if (!category) return null;
    if (category.title === "Tables" && tableScopeEnabled) {
      return renderTableScopeConfig();
    }
    return null;
  };

  return (
    <>
      <AgentConfigSection id="builtInTools">
      {enabledTools.some((toolName) =>
        ["list_agents", "list_agent_sessions", "send_message"].includes(
          toolName
        )
      ) && renderAgentScopeConfig()}

      {hasToolSearchQuery && (
        <Typography sx={{ fontSize: "11px", color: "text.secondary", mb: 1.5 }}>
          {matchingToolCount} matching tool
          {matchingToolCount === 1 ? "" : "s"} for "{toolSearchQuery.trim()}".
        </Typography>
      )}
      {staleEnabledTools.length > 0 && (
        <Alert
          severity="warning"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() =>
                setEnabledTools((prev) =>
                  prev.filter(
                    (toolName) => !staleEnabledTools.includes(toolName)
                  )
                )
              }
            >
              Remove
            </Button>
          }
          sx={{ mb: 1.5 }}
        >
          <Typography sx={{ fontSize: "12px" }}>
            {staleEnabledTools.length === 1
              ? "1 enabled tool is no longer available:"
              : `${staleEnabledTools.length} enabled tools are no longer available:`}{" "}
            <strong>{staleEnabledTools.join(", ")}</strong>. They were likely
            deleted or unexposed by the tenant.
          </Typography>
        </Alert>
      )}
      <AgentToolLoadout
        toolCategories={toolCategories}
        inventoryCategories={filteredToolCategories}
        enabledTools={enabledTools}
        onToggleTool={toggleTool}
        onBulkSetToolsEnabled={setCategoryToolsEnabled}
        toolInstructions={toolInstructions}
        onUpdateToolInstruction={(toolName, value) => {
          setToolInstructions((prev) => {
            if (value === undefined) {
              const next = { ...prev };
              delete next[toolName];
              return next;
            }
            return { ...prev, [toolName]: value };
          });
        }}
        toolSearchQuery={toolSearchQuery}
        setToolSearchQuery={setToolSearchQuery}
        loadingToolCatalog={loadingToolCatalog}
        renderToolConfig={renderToolConfig}
        renderCategoryConfig={renderCategoryConfig}
        approvalRequiredTools={approvalRequiredTools}
        onToggleToolApproval={onToggleToolApproval}
      />
      </AgentConfigSection>

      <McpToolsSection
        mcpServerRefs={mcpServerRefs}
        setMcpServerRefs={setMcpServerRefs}
      />
    </>
  );
}
